import React, { useEffect, useMemo, useState } from "react";
import { BarChart, PieChart } from "@mui/x-charts";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../../../axiosconfig.js";
import { getChatUsageTotal, getUserQuestionsAnsweredTotal } from "../../../../services/usageService.js";

// Local month boundaries for calendar display 
const startOfMonthLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const endOfMonthLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const endOfDayLocal = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

// UTC wire dates from local calendar dates (prevents timezone month/day drift)
const toUtcFromLocalDate = (d, { end = false } = {}) =>
  new Date(
    Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      end ? 23 : 0,
      end ? 59 : 0,
      end ? 59 : 0,
      end ? 999 : 0
    )
  );

// RFC-1123 / HTTP-date string
const toHttpDate = (d) => d.toUTCString();

const WaveLoadingDots = () => (
  <span className="wave-loading-dots" aria-label="Loading">
    <span className="wave-dot" />
    <span className="wave-dot" />
    <span className="wave-dot" />
  </span>
);

// Component 
const PersonalEngagementOverview = () => {
  const user = useSelector((s) => s.auth?.user);
  const userId = user?.sub || user?.id;
  const [dataViewMode, setDataViewMode] = useState("current");

  const today = useMemo(() => new Date(), []);
  const effectiveToday = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 0);
  }, []);

  const thisMonthStartLocal = useMemo(() => startOfMonthLocal(today), [today]);
  const thisMonthEndLocal = useMemo(() => endOfDayLocal(today), [today]);
  const lastCompletedMonthEnd = useMemo(
    () => endOfMonthLocal(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
    [today]
  );

  const [rangeStart, setRangeStart] = useState(thisMonthStartLocal);
  const [rangeEnd, setRangeEnd] = useState(thisMonthEndLocal);
  const [focusDate, setFocusDate] = useState(thisMonthEndLocal);

  // Metrics
  const [mindfulnessUsage, setMindfulnessUsage] = useState([0, 0]); // [minutes, exercisesCompleted]
  const [assessmentUsage, setAssessmentUsage] = useState(0); // questions answered
  const [chatUsage, setChatUsage] = useState(0); // minutes
  const [sherpaUsage, setSherpaUsage] = useState(0.0); // time tracking total in minutes (from new endpoint)
  const [liveEventsUsage, setLiveEventsUsage] = useState(0); // minutes
  const [loading, setLoading] = useState(false);

  const normalizedSherpaUsage =
    Number.isFinite(sherpaUsage)
      ? sherpaUsage
      : Number.isFinite(chatUsage)
        ? chatUsage
        : 0;

  const monthsCount = useMemo(() => {
    if (dataViewMode === "current" || !rangeStart || !rangeEnd) return 1;
    const diffYears = rangeEnd.getFullYear() - rangeStart.getFullYear();
    const diffMonths = rangeEnd.getMonth() - rangeStart.getMonth();
    return Math.max(1, diffYears * 12 + diffMonths + 1);
  }, [dataViewMode, rangeStart, rangeEnd]);

  // Assessments taken (each PEARLI assessment has 30 questions)
  const personalAssessmentsTaken = useMemo(() => {
    if (!assessmentUsage || assessmentUsage <= 0) return 0;
    return assessmentUsage >= 30 ? Math.floor(assessmentUsage / 30) : 1;
  }, [assessmentUsage]);

  const targetAssessments = monthsCount;
  const assessmentEngagementPercent = targetAssessments > 0
    ? Math.round((personalAssessmentsTaken / targetAssessments) * 100)
    : 0;

  // Live sessions attended (derived from engagement minutes, typical session ~45 min)
  const personalAttendedSessions = useMemo(() => {
    if (!liveEventsUsage || liveEventsUsage <= 0) return 0;
    return Math.max(1, Math.round(liveEventsUsage / 45));
  }, [liveEventsUsage]);

  const targetSessions = monthsCount;
  const liveSessionEngagementPercent = targetSessions > 0
    ? Math.round((personalAttendedSessions / targetSessions) * 100)
    : 0;

  // API helpers
  const postDataView = async (request, startDateUTC, endDateUTC) => {
    const res = await api.post("/dataView", {
      request,
      startDate: toHttpDate(startDateUTC),
      endDate: toHttpDate(endDateUTC),
      mode: "user",
    });
    return res?.data?.result;
  };

  const fetchAllUsage = async (startLocal, endLocal) => {
    setLoading(true);
    try {
      const normalizedStartLocal = startOfMonthLocal(new Date(startLocal));
      const normalizedEndLocal = new Date(endLocal);

      const isEndInCurrentMonth =
        normalizedEndLocal.getFullYear() === today.getFullYear() &&
        normalizedEndLocal.getMonth() === today.getMonth();

      const effectiveEndLocal = isEndInCurrentMonth
        ? endOfDayLocal(today)
        : endOfMonthLocal(normalizedEndLocal);

      // Convert picked local calendar bounds to UTC wire dates for old API
      const startUTC = toUtcFromLocalDate(normalizedStartLocal, { end: false });
      const endUTC = toUtcFromLocalDate(effectiveEndLocal, { end: true });

      // Format dates as YYYY-MM-DD for the new usage tracking endpoint
      const formatDateToYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startDateStr = formatDateToYYYYMMDD(normalizedStartLocal);
      const endDateStr = formatDateToYYYYMMDD(effectiveEndLocal);
      // Use UTC dates derived from local calendar bounds to avoid timezone day/month rollback.
      const fromDateIso = toUtcFromLocalDate(normalizedStartLocal, { end: false }).toISOString();
      const toDateIso = toUtcFromLocalDate(effectiveEndLocal, { end: true }).toISOString();

      const [mf, asmt, chat, live, sherpa] = await Promise.all([
        postDataView("mindfulnessUsage", startUTC, endUTC),
        getUserQuestionsAnsweredTotal(userId, fromDateIso, toDateIso),
        postDataView("chatUsage", startUTC, endUTC),
        postDataView("liveEventsUsage", startUTC, endUTC),
        getChatUsageTotal(startDateStr, endDateStr),
      ]);

      setMindfulnessUsage(Array.isArray(mf) ? mf : [0, 0]);
      setAssessmentUsage(typeof asmt === "number" ? asmt : 0);
      setChatUsage(typeof chat === "number" ? chat : 0);
      setLiveEventsUsage(typeof live === "number" ? live : 0);
      setSherpaUsage(typeof sherpa === "number" ? sherpa : 0.0);
    } catch (e) {
      console.error(e);
      setMindfulnessUsage([0, 0]);
      setAssessmentUsage(0);
      setChatUsage(0);
      setLiveEventsUsage(0);
      setSherpaUsage(0.0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchAllUsage(thisMonthStartLocal, thisMonthEndLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Handlers 
  const handleIntervalChange = (mode) => {
    setDataViewMode(mode);

    if (mode === "current") {
      setRangeStart(thisMonthStartLocal);
      setRangeEnd(thisMonthEndLocal);
      setFocusDate(thisMonthEndLocal);
      if (userId) {
        fetchAllUsage(thisMonthStartLocal, thisMonthEndLocal);
      }
    }

    if (mode === "range") {
      const prev2Start = startOfMonthLocal(
        new Date(effectiveToday.getFullYear(), effectiveToday.getMonth() - 1, 1)
      );
      const prev1End = endOfMonthLocal(effectiveToday);

      setRangeStart(prev2Start);
      setRangeEnd(prev1End);
      setFocusDate(prev1End);
      if (userId) {
        fetchAllUsage(prev2Start, prev1End);
      }
    }
  };

  const handleRangePick = (dates) => {
    const [start, end] = dates || [];
    const safeStart = start
      ? new Date(Math.min(start.getTime(), lastCompletedMonthEnd.getTime()))
      : null;
    const safeEnd = end
      ? new Date(Math.min(end.getTime(), lastCompletedMonthEnd.getTime()))
      : null;

    if (safeStart && safeEnd && safeStart > safeEnd) {
      return;
    }

    setRangeStart(safeStart);
    setRangeEnd(safeEnd);
    if (safeStart) setFocusDate(safeStart);

    if (safeStart && safeEnd) {
      const normalizedStart = startOfMonthLocal(safeStart);
      const normalizedEnd = endOfMonthLocal(safeEnd);
      if (userId) {
        fetchAllUsage(normalizedStart, normalizedEnd);
      }
    }
  };


  return (
    <div className="home-statistics-group-container">
      <div className="home-statistics-section-container">
        <div className="home-statistics-section-header">
          <div className="home-statistics-section-text-container">
            <div className="home-statistics-section-title">
              Personal Engagement Overview
            </div>
            <div className="home-statistics-section-subtitle">
              Track how different Mindsherpa features support daily well-being habits
            </div>
          </div>

          <div className="home-menu-container">
            <div className="home-statistics-section-interval-button-container">
              <div
                className={`home-statistics-section-interval-button ${dataViewMode === "current" ? "active" : ""
                  }`}
                onClick={() => handleIntervalChange("current")}
              >
                Current Month
              </div>
              <div
                className={`home-statistics-section-interval-button ${dataViewMode === "range" ? "active" : ""
                  }`}
                onClick={() => handleIntervalChange("range")}
              >
                Month Range
              </div>
            </div>

            <div
              className="home-daterange-button"
              id="daterange"
              style={{ display: dataViewMode === "range" ? "flex" : "none" }}
            >
              <DatePicker
                id="datepicker"
                className="home-datepicker"
                selectsRange
                showMonthYearPicker
                dateFormat="MMMM yyyy"
                startDate={rangeStart || undefined}
                endDate={rangeEnd || undefined}
                onChange={handleRangePick}
                minDate={new Date(2023, 0, 1)}
                maxDate={lastCompletedMonthEnd}
                openToDate={focusDate}
              />
              <div className="home-daterange-button-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.2 9.6C7.97333 9.6 7.78347 9.5232 7.6304 9.3696C7.4768 9.21653 7.4 9.02667 7.4 8.8C7.4 8.57333 7.4768 8.3832 7.6304 8.2296C7.78347 8.07653 7.97333 8 8.2 8C8.42667 8 8.6168 8.07653 8.7704 8.2296C8.92347 8.3832 9 8.57333 9 8.8C9 9.02667 8.92347 9.21653 8.7704 9.3696C8.6168 9.5232 8.42667 9.6 8.2 9.6ZM5 9.6C4.77333 9.6 4.5832 9.5232 4.4296 9.3696C4.27653 9.21653 4.2 9.02667 4.2 8.8C4.2 8.57333 4.27653 8.3832 4.4296 8.2296C4.5832 8.07653 4.77333 8 5 8C5.22667 8 5.4168 8.07653 5.5704 8.2296C5.72347 8.3832 5.8 8.57333 5.8 8.8C5.8 9.02667 5.72347 9.21653 5.5704 9.3696C5.4168 9.5232 5.22667 9.6 5 9.6ZM11.4 9.6C11.1733 9.6 10.9835 9.5232 10.8304 9.3696C10.6768 9.21653 10.6 9.02667 10.6 8.8C10.6 8.57333 10.6768 8.3832 10.8304 8.2296C10.9835 8.07653 11.1733 8 11.4 8C11.6267 8 11.8165 8.07653 11.9696 8.2296C12.1232 8.3832 12.2 8.57333 12.2 8.8C12.2 9.02667 12.1232 9.21653 11.9696 9.3696C11.8165 9.5232 11.6267 9.6 11.4 9.6ZM8.2 12.8C7.97333 12.8 7.78347 12.7232 7.6304 12.5696C7.4768 12.4165 7.4 12.2267 7.4 12C7.4 11.7733 7.4768 11.5835 7.6304 11.4304C7.78347 11.2768 7.97333 11.2 8.2 11.2C8.42667 11.2 8.6168 11.2768 8.7704 11.4304C8.92347 11.5835 9 11.7733 9 12C9 12.2267 8.92347 12.4165 8.7704 12.5696C8.6168 12.7232 8.42667 12.8 8.2 12.8ZM5 12.8C4.77333 12.8 4.5832 12.7232 4.4296 12.5696C4.27653 12.4165 4.2 12.2267 4.2 12C4.2 11.7733 4.27653 11.5835 4.4296 11.4304C4.5832 11.2768 4.77333 11.2 5 11.2C5.22667 11.2 5.4168 11.2768 5.5704 11.4304C5.72347 11.5835 5.8 11.7733 5.8 12C5.8 12.2267 5.72347 12.4165 5.5704 12.5696C5.4168 12.7232 5.22667 12.8 5 12.8ZM11.4 12.8C11.1733 12.8 10.9835 12.7232 10.8304 12.5696C10.6768 12.4165 10.6 12.2267 10.6 12C10.6 11.7733 10.6768 11.5835 10.8304 11.4304C10.9835 11.2768 11.1733 11.2 11.4 11.2C11.6267 11.2 11.8165 11.2768 11.9696 11.4304C12.1232 11.5835 12.2 11.7733 12.2 12C12.2 12.2267 12.1232 12.4165 11.9696 12.5696C11.8165 12.7232 11.6267 12.8 11.4 12.8ZM2.6 16C2.16 16 1.7832 15.8435 1.4696 15.5304C1.15653 15.2168 1 14.84 1 14.4V3.2C1 2.76 1.15653 2.38347 1.4696 2.0704C1.7832 1.7568 2.16 1.6 2.6 1.6H3.4V0H5V1.6H11.4V0H13V1.6H13.8C14.24 1.6 14.6168 1.7568 14.9304 2.0704C15.2435 2.38347 15.4 2.76 15.4 3.2V14.4C15.4 14.84 15.2435 15.2168 14.9304 15.5304C14.6168 15.8435 14.24 16 13.8 16H2.6ZM2.6 14.4H13.8V6.4H2.6V14.4Z" fill="#AAAAAA" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tiles */}
        <div className="home-statistics-section-item-container personal-engagement-tiles">
          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                Sherpa Usage
              </div>
              <div className="home-statistics-section-item-subtitle">
                Engagement in Minutes
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : `${normalizedSherpaUsage}`}
              </div>
            </div>
          </div>

          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">Mindfulness</div>
              <div className="home-statistics-section-item-subtitle">
                Engagement in Minutes
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : mindfulnessUsage[0]}
              </div>
            </div>
          </div>

          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">Exercises</div>
              <div className="home-statistics-section-item-subtitle">
                Mindfulness Exercises Completed
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : mindfulnessUsage[1]}
              </div>
            </div>
          </div>

          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">
                PEARLI Assessments
              </div>
              <div className="home-statistics-section-item-subtitle">
                Number of questions answered
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : assessmentUsage}
              </div>
            </div>
          </div>

          <div className="home-statistics-section-item">
            <div className="home-statistics-section-item-header">
              <div className="home-statistics-section-item-title">Live Sessions</div>
              <div className="home-statistics-section-item-subtitle">
                Engagement in minutes
              </div>
            </div>
            <div className="home-statistics-section-item-body-container">
              <div className="home-statistics-section-item-body-text">
                {loading ? <WaveLoadingDots /> : liveEventsUsage}
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Charts Row */}
        <div className="home-statistics-section-item-container personal-engagement-charts-container">
          {/* Card 1: Personal Engagement Bar Chart */}
          <div className="personal-engagement-bar-card">
            <div className="personal-engagement-card-header">
              <div className="personal-engagement-card-title">
                Personal Engagement
              </div>
              <div className="personal-engagement-card-subtitle">
                Total time spent per feature in minutes
              </div>
            </div>
            <div className="home-statistics-section-item-split-body-container">
              <div className="personal-engagement-chart-box">
                <BarChart
                  loading={loading}
                  xAxis={[
                    {
                      scaleType: "band",
                      data: ["Sherpa", "Mindfulness", "Live Sessions"],
                      label:
                        "Sherpa usage compared with mindfulness and live sessions",
                    },
                  ]}
                  yAxis={[{ label: "Total Minutes" }]}
                  series={[
                    {
                      data: [normalizedSherpaUsage, mindfulnessUsage[0], liveEventsUsage],
                      color: "#60A5FA",
                    },
                  ]}
                  height={300}
                  barLabel={(item) =>
                    item.value > 0 ? `${item.value} min.` : ""
                  }
                />
              </div>
            </div>
          </div>

          {/* Card 2: PEARLI Assessment Engagement */}
          <div className="personal-engagement-pie-card">
            <div className="personal-engagement-card-header">
              <div className="personal-engagement-card-title">
                PEARLI Assessment Engagement
              </div>
            </div>
            <div className="personal-pie-body-container-graph">
              <div className="personal-piechart">
                <p className="personal-piechart-text">
                  {personalAssessmentsTaken}/{targetAssessments}
                </p>
                <p className="personal-piechart-subtext">
                  Assessments Taken
                </p>
                <PieChart
                  loading={loading}
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: personalAssessmentsTaken,
                          color: "#19B880",
                        },
                        {
                          id: 1,
                          value: Math.max(0, targetAssessments - personalAssessmentsTaken),
                          color: "#D3F8E4",
                        },
                      ],
                      innerRadius: 70,
                      cornerRadius: 80,
                      startAngle: -90,
                      endAngle: 270,
                    },
                  ]}
                  width={200}
                  height={200}
                />
              </div>
              <div className="personal-pie-body-text">
                {assessmentEngagementPercent}% Engagement
              </div>
            </div>
          </div>

          {/* Card 3: Live Session Engagement */}
          <div className="personal-engagement-pie-card">
            <div className="personal-engagement-card-header">
              <div className="personal-engagement-card-title">
                Live Session Engagement
              </div>
            </div>
            <div className="personal-pie-body-container-graph">
              <div className="personal-piechart">
                <p className="personal-piechart-text">
                  {personalAttendedSessions}/{targetSessions}
                </p>
                <p className="personal-piechart-subtext">
                  Attended Sessions
                </p>
                <PieChart
                  loading={loading}
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: personalAttendedSessions,
                          color: "#19B880",
                        },
                        {
                          id: 1,
                          value: Math.max(0, targetSessions - personalAttendedSessions),
                          color: "#D3F8E4",
                        },
                      ],
                      innerRadius: 70,
                      cornerRadius: 80,
                      startAngle: -90,
                      endAngle: 270,
                    },
                  ]}
                  width={200}
                  height={200}
                />
              </div>
              <div className="personal-pie-body-text">
                {liveSessionEngagementPercent}% Engagement
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalEngagementOverview;
